/**
 * Calendar Logic Template (CLT) — front-end types + helpers.
 *
 * Most data flows through React Query against the real backend now; the only
 * mock data still here is the prototype reference list `MILESTONES` and the
 * `CALENDAR_LOOKUP` re-export — both used by the client-side instantiation
 * engine on `/admin/clt-calendars/:id` (a documented temporary shortcut; the
 * canonical date engine is `GET /calendars/{id}/dates`).
 */
import type { DynamicCoefficient } from '@/api/clt.types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ValueChooserMode = 'DELTA' | 'BACKUP_DAYS' | 'CALENDAR_LOOKUP'

export type CalendarColumn = 'paw_end' | 'pog_complete'

export interface ValueChooserRule {
  mode: ValueChooserMode
  dynamic: DynamicCoefficient
  fromMilestoneId?: string
  deltaDays?: number
  calendarColumn?: CalendarColumn
}

export function defaultDynamicForMode(
  mode: ValueChooserMode
): DynamicCoefficient {
  return mode === 'BACKUP_DAYS' ? 1 : 0
}

/** Hidden coefficients survive edits unless the rule's final mode changes. */
export function dynamicForRuleEdit(
  mode: ValueChooserMode,
  previous?: ValueChooserRule | null
): DynamicCoefficient {
  return previous?.mode === mode
    ? previous.dynamic
    : defaultDynamicForMode(mode)
}

export type SubstepKey =
  | 'IMPORT_RAIL'
  | 'IMPORT_NO_RAIL'
  | 'DOMESTIC_RAIL'
  | 'DOMESTIC_NO_RAIL'

export const SUBSTEPS: {
  key: SubstepKey
  label: string
  group: 'Import' | 'Domestic'
  rail: boolean
}[] = [
  { key: 'IMPORT_RAIL', label: 'Import · Rail', group: 'Import', rail: true },
  {
    key: 'IMPORT_NO_RAIL',
    label: 'Import · No Rail',
    group: 'Import',
    rail: false,
  },
  {
    key: 'DOMESTIC_RAIL',
    label: 'Domestic · Rail',
    group: 'Domestic',
    rail: true,
  },
  {
    key: 'DOMESTIC_NO_RAIL',
    label: 'Domestic · No Rail',
    group: 'Domestic',
    rail: false,
  },
]

export type CltMatrix = Record<string, ValueChooserRule>

export const makeEmptyMatrices = (): Record<SubstepKey, CltMatrix> => ({
  IMPORT_RAIL: {},
  IMPORT_NO_RAIL: {},
  DOMESTIC_RAIL: {},
  DOMESTIC_NO_RAIL: {},
})

export interface StrategyGroup {
  id: string
  label: string
  codes: string[]
}

export type TargetScope = 'ALL' | 'SPECIFIC'

export interface ApplyTarget {
  calendarId: string
  scope: TargetScope
  eventIds: string[]
}

export interface BiweeklyTarget {
  id: string
  strategyGroupId: string
  isImport: boolean
  isRail: boolean
}

export interface BiweeklyOffsetGroup {
  id: string
  label: string
  offsetDays: number
  milestoneIds: string[]
  targets: BiweeklyTarget[]
}

export interface CalendarLogicTemplate {
  id: string
  name: string
  description?: string
  strategyGroups: StrategyGroup[]
  matrices: Record<SubstepKey, CltMatrix>
  biweeklyOffset: number
  biweeklyOffsetGroups: BiweeklyOffsetGroup[]
  appliesToWeeks: number[]
  applyTargets: ApplyTarget[]
  isDefault: boolean
  updatedAt: string
}

export interface Labeled {
  id: string
  label: string
}

// ---------------------------------------------------------------------------
// Prototype reference data — only consumed by the client-side instantiation
// engine on the CLT calendar detail page. Real lists come from the API.
// ---------------------------------------------------------------------------

/** Rows of the Step 1 matrix (for the prototype date engine only). */
export const MILESTONES: Labeled[] = [
  { id: 'm-1', label: 'Assortment Plan Approved' },
  { id: 'm-2', label: 'Purchase Plan / Financials Approved' },
  { id: 'm-3', label: 'Commitment Letters Due' },
  { id: 'm-4', label: 'Quote Sheets Due' },
  { id: 'm-5', label: 'SKU Set Up Due' },
  { id: 'm-6', label: 'POs Due' },
  { id: 'm-7', label: 'Vendor in Production' },
  { id: 'm-8', label: 'Vendor Booked Transportation' },
  { id: 'm-9', label: 'Vendor On Time (Cargo Ready Date)' },
  { id: 'm-10', label: 'Container In Gate' },
  { id: 'm-11', label: 'Vessel Departure' },
  { id: 'm-12', label: 'Shipment Arrived at Port of Discharge' },
  { id: 'm-14', label: 'Unload Vessel / Discharge Date' },
  { id: 'm-15', label: 'Rail Departure' },
  { id: 'm-16', label: 'Rail Arrival' },
  { id: 'm-17', label: 'Shipment Pickup' },
  { id: 'm-18', label: 'Attached to POG' },
  { id: 'm-19', label: 'Product allocated' },
  { id: 'm-20', label: 'Deconsolidation DC Arrival (Hazelton)' },
  { id: 'm-21', label: 'Deconsolidation Trailer Loaded - Ready for Pickup' },
  { id: 'm-22', label: 'Deconsolidation DC Departure (Hazelton)' },
  { id: 'm-23', label: 'Product Arrived (Inbound)' },
  { id: 'm-24', label: 'Product Received (Inbound)' },
  { id: 'm-25', label: 'Picked' },
  { id: 'm-26', label: 'Loaded' },
  { id: 'm-27', label: 'Ship to Store (Outbound)' },
  { id: 'm-28', label: 'In Store Date' },
  { id: 'm-29', label: 'POG Complete Date' },
]

/**
 * Canonical display order for milestones in the Milestone Logic matrix. The
 * milestones list itself comes from the API; this just dictates row order.
 * Milestones whose labels are not in this list are appended at the end in the
 * order returned by the API.
 */
export const MILESTONE_ORDER: string[] = [
  'Assortment Plan Approved',
  'Purchase Plan / Financials Approved',
  'Commitment Letters Due',
  'Quote Sheets Due',
  'SKU Set Up Due',
  'POs Due',
  'Vendor in Production',
  'Vendor Booked Transportation',
  'Vendor On Time (Cargo Ready Date)',
  'Shipment Consolidated',
  'Vessel Departure',
  'Shipment Arrived at Port of Discharge',
  'Shipment Arrived at Port of Arrival',
  'Unload Vessel / Discharge Date',
  'Rail Departure',
  'Rail Arrival',
  'Shipment Pickup',
  'Attached to POG',
  'Product allocated',
  'Deconsolidation DC Arrival (Hazelton)',
  'Deconsolidation Trailer Loaded - Ready for Pickup',
  'Deconsolidation DC Departure (Hazelton)',
  'Product Arrived (Inbound)',
  'Product Received (Inbound)',
  'Picked',
  'Loaded',
  'Ship to Store (Outbound)',
  'In Store Date',
  'POG Complete Date',
]

const MILESTONE_ORDER_INDEX = new Map(MILESTONE_ORDER.map((l, i) => [l, i]))

/**
 * Sort a milestone list by MILESTONE_ORDER (by label). Unknown labels keep
 * their relative API order and are appended after the known ones.
 */
export function sortMilestonesByOrder<T extends { label: string }>(
  list: T[]
): T[] {
  return [...list].sort((a, b) => {
    const ai = MILESTONE_ORDER_INDEX.get(a.label) ?? Number.POSITIVE_INFINITY
    const bi = MILESTONE_ORDER_INDEX.get(b.label) ?? Number.POSITIVE_INFINITY
    return ai - bi
  })
}

/** Deep-clone a set of groups so editing a draft never mutates the source. */
export const cloneStrategyGroups = (groups: StrategyGroup[]): StrategyGroup[] =>
  groups.map(g => ({ ...g, codes: [...g.codes] }))

/** Fresh id for a newly-added group. */
export const makeStrategyGroupId = () => `sg-${crypto.randomUUID().slice(0, 8)}`

/** Auto-suggested label for a group from its codes (user can override). */
export const suggestGroupLabel = (codes: string[]) =>
  codes.length === 0 ? 'New group' : `Strategy Code ${codes.join(', ')}`

/** Fresh id for a biweekly offset group. */
export const makeBiweeklyGroupId = () => `bw-${crypto.randomUUID().slice(0, 8)}`

/** Fresh id for a single biweekly target (one strategy-group + lane pairing). */
export const makeBiweeklyTargetId = () =>
  `bwt-${crypto.randomUUID().slice(0, 8)}`

/** Default label for a freshly-added biweekly group (index is zero-based). */
export const suggestBiweeklyLabel = (index: number) =>
  `Biweekly group ${index + 1}`

/** Deep-clone biweekly offset groups so editing a draft never mutates source. */
export const cloneBiweeklyOffsetGroups = (
  groups: BiweeklyOffsetGroup[]
): BiweeklyOffsetGroup[] =>
  groups.map(g => ({
    ...g,
    milestoneIds: [...g.milestoneIds],
    targets: g.targets.map(t => ({ ...t })),
  }))

/**
 * Local backup-days row shape used by template-editor scaffolding. The API
 * shape (`@/api/clt.types.BackupDaysRow`) has a numeric `wh` and nullable legs;
 * callers normalize between the two as needed.
 */
export interface BackupDaysRow {
  origin: string
  wh: string
  shipping: number
  rail: number
  truck: number
}

export interface CalendarLookupRow {
  year: string
  event: string
  dc: string
  paw_end: string
  pog_complete: string
}

export { CALENDAR_LOOKUP_ROWS as CALENDAR_LOOKUP } from './calendar-lookup.data'

export const CALENDAR_COLUMNS: { value: CalendarColumn; label: string }[] = [
  { value: 'paw_end', label: 'PAW End' },
  { value: 'pog_complete', label: 'POG Complete' },
]

export const calendarColumnLabel = (col?: CalendarColumn | null) =>
  CALENDAR_COLUMNS.find(c => c.value === col)?.label ?? '—'

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

export const cellKey = (milestoneId: string, strategyId: string) =>
  `${milestoneId}__${strategyId}`

export const labelOf = (list: Labeled[], id: string | undefined | null) =>
  list.find(item => item.id === id)?.label ?? '—'

/**
 * Best-effort label for a milestone id. Falls back to '—' for backend ids that
 * are not in the prototype MILESTONES list — only the prototype date engine
 * (detail page) is affected by this. Real labels flow through `useMilestones`
 * in the wizard.
 */
export const milestoneLabel = (id?: string | null) => labelOf(MILESTONES, id)

/** Short summary of where a template applies (for cards + review). */
export function applyTargetsSummary(targets: ApplyTarget[]): string {
  if (targets.length === 0) return 'No calendars'
  const fullCount = targets.filter(t => t.scope === 'ALL').length
  const eventCount = targets
    .filter(t => t.scope === 'SPECIFIC')
    .reduce((sum, t) => sum + t.eventIds.length, 0)
  const parts: string[] = []
  if (fullCount)
    parts.push(`${fullCount} full calendar${fullCount === 1 ? '' : 's'}`)
  if (eventCount)
    parts.push(`${eventCount} event${eventCount === 1 ? '' : 's'}`)
  return parts.length
    ? parts.join(' · ')
    : `${targets.length} calendar${targets.length === 1 ? '' : 's'}`
}

/** Short human-readable summary of a single cell rule (for grid cells). */
export function summarizeRule(
  rule: ValueChooserRule | undefined,
  milestones?: Labeled[]
): string {
  if (!rule) return ''
  const resolveMilestone = (id?: string | null) =>
    milestones ? labelOf(milestones, id) : milestoneLabel(id)
  switch (rule.mode) {
    case 'DELTA': {
      const sign = (rule.deltaDays ?? 0) >= 0 ? '+' : ''
      return `${sign}${rule.deltaDays ?? 0}d from ${resolveMilestone(rule.fromMilestoneId)}`
    }
    case 'BACKUP_DAYS': {
      const off = rule.deltaDays
      const offStr = off ? ` ${off >= 0 ? '+' : ''}${off}d` : ''
      return `Dynamic${offStr} from ${resolveMilestone(rule.fromMilestoneId)}`
    }
    case 'CALENDAR_LOOKUP':
      return `Calendar: ${calendarColumnLabel(rule.calendarColumn)}`
    default:
      return ''
  }
}

/**
 * A rule is invalid if the backend would reject it on save. Mirrors the
 * per-mode requirements enforced by `ValueChooserPopover`'s Apply button, but
 * also catches rules that slipped into state via bulk Fill / Copy From or were
 * loaded from an existing template — DELTA / BACKUP_DAYS rules whose
 * `fromMilestoneId` is missing or self-referential cause a 400 at submit
 * ("DELTA rules require a vs_milestone_id").
 */
export function isRuleInvalid(
  rule: ValueChooserRule,
  milestoneId: string
): boolean {
  switch (rule.mode) {
    case 'DELTA':
      return (
        !rule.fromMilestoneId ||
        rule.fromMilestoneId === milestoneId ||
        !Number.isFinite(rule.deltaDays)
      )
    case 'BACKUP_DAYS':
      return !rule.fromMilestoneId || rule.fromMilestoneId === milestoneId
    case 'CALENDAR_LOOKUP':
      return !rule.calendarColumn
    default:
      return false
  }
}

export interface InvalidRuleCell {
  substepKey: SubstepKey
  substepLabel: string
  milestoneId: string
  strategyGroupId: string
}

/** Walk all 4 substep matrices and list every cell whose rule is invalid. */
export function collectInvalidRules(
  matrices: Record<SubstepKey, CltMatrix>
): InvalidRuleCell[] {
  const invalid: InvalidRuleCell[] = []
  for (const sub of SUBSTEPS) {
    const matrix = matrices[sub.key]
    for (const [key, rule] of Object.entries(matrix)) {
      const [milestoneId, strategyGroupId] = key.split('__')
      if (isRuleInvalid(rule, milestoneId)) {
        invalid.push({
          substepKey: sub.key,
          substepLabel: sub.label,
          milestoneId,
          strategyGroupId,
        })
      }
    }
  }
  return invalid
}

export const VALUE_CHOOSER_MODES: { value: ValueChooserMode; label: string }[] =
  [
    { value: 'DELTA', label: 'Delta in days from another milestone' },
    {
      value: 'BACKUP_DAYS',
      label: 'Dynamic',
    },
    { value: 'CALENDAR_LOOKUP', label: 'Select from Calendar' },
  ]

/**
 * Per-rule-type cell colors for the matrix. Delta (the common case) is a neutral
 * gray default; Dynamic and Calendar each get a distinct accent so the three rule
 * kinds read apart at a glance. Value = filled-cell border/bg/hover classes.
 */
export const RULE_MODE_STYLE: Record<ValueChooserMode, string> = {
  DELTA:
    'border-border bg-muted/60 hover:border-muted-foreground/50 hover:bg-muted',
  BACKUP_DAYS:
    'border-blue-400/60 bg-blue-50 hover:border-blue-500 hover:bg-blue-100/70',
  CALENDAR_LOOKUP:
    'border-emerald-400/60 bg-emerald-50 hover:border-emerald-500 hover:bg-emerald-100/70',
}
